import type Highcharts from 'highcharts';
import { baseChartOptions } from './chartDefaults';
import { CurvePoint, splitCurveAtAge } from './curveSeries';

type TranslateFn = (key: string) => string;

/**
 * Build the Highcharts tooltip formatter for the asset survival curve. Kept as a
 * module-level factory (not inline in the dialog) so the `this`-based Highcharts
 * callback does not live inside a React function component.
 */
export const createCurveTooltipFormatter = (t: TranslateFn) =>
	function (this: Highcharts.Point): string {
		return `<b>${t('Age')}: ${this.x} ${t('yrs')}</b><br/>${t('FP')}: ${Number(this.y).toFixed(1)}%`;
	};

/**
 * Build the Highcharts options for the asset survival curve: a solid history line up
 * to the asset's current age, a dashed projection beyond it, the 80% failure-threshold
 * marker, and a scatter point at the asset's current position.
 */
export const buildCurveChartOptions = (
	curveData: CurvePoint[],
	currentAge: number,
	currentFP: number,
	t: TranslateFn,
): Highcharts.Options => {
	const { historyData, projectionData } = splitCurveAtAge(curveData, currentAge);

	return {
		...baseChartOptions,
		chart: { type: 'line', height: 300, animation: false },
		title: { text: '' },
		legend: { enabled: false },
		xAxis: {
			title: { text: t('Asset Age (Years)') },
			plotLines: [
				{
					value: currentAge,
					color: '#9ca3af',
					dashStyle: 'Dash' as Highcharts.DashStyleValue,
					width: 1,
					zIndex: 3,
				},
			],
		},
		yAxis: {
			title: { text: t('Failure Probability (%)') },
			min: 0,
			max: 100,
			plotLines: [
				{
					value: 80,
					color: '#dc2626',
					dashStyle: 'Dash' as Highcharts.DashStyleValue,
					width: 1.5,
					zIndex: 5,
					label: {
						text: '80%',
						align: 'right',
						style: { color: '#dc2626', fontSize: '11px' },
					},
				},
			],
		},
		tooltip: {
			formatter: createCurveTooltipFormatter(t),
		},
		series: [
			{
				name: t('History'),
				data: historyData,
				type: 'line',
				color: '#3b82f6',
				lineWidth: 2.5,
				dashStyle: 'Solid' as Highcharts.DashStyleValue,
				marker: { enabled: false },
			},
			{
				name: t('Projection'),
				data: projectionData,
				type: 'line',
				color: '#3b82f6',
				lineWidth: 2,
				dashStyle: 'Dash' as Highcharts.DashStyleValue,
				marker: { enabled: false },
			},
			{
				name: t('Current Position'),
				data: [[currentAge, currentFP]],
				type: 'scatter',
				color: '#3b82f6',
				marker: {
					enabled: true,
					radius: 6,
					symbol: 'circle',
					lineColor: '#ffffff',
					lineWidth: 2,
				},
				showInLegend: false,
				enableMouseTracking: false,
				zIndex: 10,
			},
		],
	};
};
